import { Clock, ArrowRight, CheckCircle, XCircle, Zap } from 'lucide-react';

const TimelineComparison = () => {
  const manualTimeline = [
    { month: 'Month 1-2', task: 'Requirements gathering', pain: 'Back-and-forth emails, unclear scope, multiple revision cycles' },
    { month: 'Month 2-3', task: 'Market research', pain: 'Hunting for past performance data across disconnected systems' },
    { month: 'Month 3-4', task: 'Drafting RFP', pain: 'Legal review ping-pong, compliance gaps found late' },
    { month: 'Month 4-5', task: 'Solicitation posted', pain: 'Q&A management chaos, amendment after amendment' },
    { month: 'Month 5-6', task: 'Proposal evaluation', pain: 'Scoring debates, inconsistent documentation' },
    { month: 'Month 6-7', task: 'Source selection', pain: 'Building protest-proof documentation from scratch' },
    { month: 'Month 7-8', task: 'Award... finally', pain: 'Maybe. If no protests.' }
  ];

  const aspTimeline = [
    { week: 'Week 1', task: 'Requirements + Market Research + Draft RFP', result: 'AI-assisted drafting, real-time collaboration, instant compliance checks' },
    { week: 'Week 2', task: 'Legal/Compliance Review + Finalize', result: 'AI pre-checked for FAR/DFARS, structured review workflow' },
    { week: 'Week 3', task: 'Evaluation + Source Selection + Award', result: 'AI-assisted scoring, defensible documentation auto-generated' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 font-medium rounded-full mb-6">
            <Clock className="w-5 h-5" />
            <span>The Timeline Truth</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            8 Months vs.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">3 Weeks</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Same acquisition. Same requirements. Radically different experience.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Manual Timeline */}
          <div className="bg-gray-50 rounded-2xl p-8 border-2 border-red-200">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">The Manual Way</h3>
                <p className="text-red-600 font-semibold">4-8 months of pain</p>
              </div>
            </div>

            <div className="space-y-4">
              {manualTimeline.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-24">
                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded">
                      {item.month}
                    </span>
                  </div>
                  <div className="flex-1 pb-4 border-b border-gray-200 last:border-0">
                    <div className="font-semibold text-gray-900 mb-1">{item.task}</div>
                    <div className="text-sm text-gray-500 italic">"{item.pain}"</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-red-100 rounded-xl">
              <div className="text-red-800 font-semibold">Total Time: 4-8 months</div>
              <div className="text-red-600 text-sm">Plus risk of protests, rework, and missed funding windows</div>
            </div>

            {/* Stats to balance with ASP side */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-2xl font-bold text-red-600">100%</div>
                <div className="text-xs text-gray-600">Manual effort required</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-2xl font-bold text-red-600">30-40%</div>
                <div className="text-xs text-gray-600">Compliance catch rate</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-2xl font-bold text-red-600">47+</div>
                <div className="text-xs text-gray-600">Handoffs between teams</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <div className="text-2xl font-bold text-red-600">Scattered</div>
                <div className="text-xs text-gray-600">Audit trail coverage</div>
              </div>
            </div>

            {/* Pain points to balance */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-gray-700">Errors discovered late in the process</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-gray-700">Version control nightmares across teams</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-gray-700">Protest documentation built reactively</span>
              </div>
            </div>
          </div>

          {/* ASP Timeline */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 border-2 border-purple-300">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">The AcuSightPro Way</h3>
                <p className="text-purple-600 font-semibold">1-3 weeks to award</p>
              </div>
            </div>

            <div className="space-y-6">
              {aspTimeline.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-5 shadow-sm border border-purple-100">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-1 rounded-full">
                      {item.week}
                    </span>
                    <span className="font-semibold text-gray-900">{item.task}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600">{item.result}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl text-white">
              <div className="font-semibold">Total Time: 1-3 weeks</div>
              <div className="text-purple-100 text-sm">With audit trail, compliance checks, and protest-ready documentation</div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">70-85%</div>
                <div className="text-xs text-gray-600">Admin work automated</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-emerald-600">95%+</div>
                <div className="text-xs text-gray-600">Compliance catch rate</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-blue-600">Zero</div>
                <div className="text-xs text-gray-600">Handoff delays</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">100%</div>
                <div className="text-xs text-gray-600">Audit trail coverage</div>
              </div>
            </div>

            {/* Key Benefits */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">ACUMEN-7 AI catches issues before they become problems</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Real-time collaboration. No waiting on email replies</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Protest-ready documentation generated automatically</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Single source of truth. No version control chaos</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Built-in FAR/DFARS compliance validation</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Automated workflow keeps everyone aligned</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Risk alerts before funding windows close</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Self-healing AI models adapt to your agency's needs</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Instant market research from verified databases</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Smart templates pre-filled with your data</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
            That&apos;s not efficiency. That&apos;s a{' '}
            <span className="animated-gradient-text">transformation</span>.
          </p>
          <p className="text-lg sm:text-xl text-gray-600">Same team. Same compliance requirements. 10x faster.</p>
        </div>
      </div>
    </section>
  );
};

export default TimelineComparison;
